import {
  IQuoteData,
  IQuotePackage,
  IQuotePackageData,
  IQuotePackageDocument,
  IQuotePackageDocumentData
} from '@/models';
import {
  IQuoteService,
  IQuotePaginatedListData,
  IQuoteXMLOptions,
  IQuoteCalculateCoreDiscountPercentParams,
  IQuoteApplyDiscountPercentagesParams,
  IQuotePackageParams,
  IQuoteFilterParam
} from './quote.types';
import { QUOTE_EXPORT_TIMEOUT_IN_MILLISECONDS } from './quote.constants';
import { AxiosInstance } from 'axios';
import { IConfigurationItemData } from '@/models/configurator/configuration';
import { Transition } from '@/models/transition';
import { objectToFormData } from '@/utils/objectToFormData';
import { convertFilterToAxiosParams } from '@/utils/helpers';
import { QuoteSurchargeApplicationTypeEnum } from '@/models/quote/quote.constants';

export class QuoteService implements IQuoteService {
  private endpoint: string;

  constructor(private axios: AxiosInstance) {
    this.endpoint = 'v1/quotes/proposals/';
  }

  createQuote(data: IQuoteData): Promise<IQuoteData> {
    return this.axios
      .post<IQuoteData>(`${this.endpoint}`, data)
      .then(({ data }) => data);
  }

  fetchQuotes(filter?: IQuoteFilterParam): Promise<IQuotePaginatedListData> {
    //By default axios converts list parameters to 'key[]=value'... this is not compatible with django.
    //this is why we use URLSearchParams to convert the list to 'key=value'
    const queryParams = convertFilterToAxiosParams(filter);

    return this.axios
      .get<IQuotePaginatedListData>(`${this.endpoint}`, {
        timeout: 1 * (60 * 1000), //1 minute
        params: queryParams
      })
      .then(({ data }) => data);
  }

  fetchQuote(id: number): Promise<IQuoteData> {
    return this.axios
      .get<IQuoteData>(`${this.endpoint}${id}/`)
      .then(({ data }) => data);
  }

  updateQuote(data: IQuoteData): Promise<IQuoteData> {
    return this.axios
      .patch<IQuoteData>(`${this.endpoint}${data.number}/`, data)
      .then(({ data }) => data);
  }

  deleteQuote(id: number): Promise<any> {
    return this.axios.delete(`${this.endpoint}${id}/`);
  }

  // Quote Revisions
  fetchQuoteRevisions(id: number): Promise<IQuoteData[]> {
    return this.axios
      .get<IQuoteData[]>(`${this.endpoint}${id}/revisions/`)
      .then(({ data }) => data);
  }

  fetchQuoteRevision(quoteId: number, revisionId: number): Promise<IQuoteData> {
    return this.axios
      .get<IQuoteData>(`${this.endpoint}${quoteId}/revisions/${revisionId}/`)
      .then(({ data }) => data);
  }

  createQuoteRevision(id: number, quote: IQuoteData): Promise<IQuoteData> {
    return this.axios
      .post<IQuoteData>(`${this.endpoint}${id}/create_new_revision/`, quote)
      .then(({ data }) => data);
  }

  setLatestRevision(quoteId: number, revisionId: number): Promise<IQuoteData> {
    return this.axios
      .put<IQuoteData>(
        `${this.endpoint}${quoteId}/revisions/${revisionId}/set_latest/`
      )
      .then(({ data }) => data);
  }

  exportQuoteItems(quoteId: number, revisionId: number): Promise<IQuoteData> {
    return this.axios
      .get<IQuoteData>(
        `${this.endpoint}${quoteId}/revisions/${revisionId}/export-items/`,
        {
          responseType: 'blob',
          timeout: 60 * 1000 * 1 //1 mins
        }
      )
      .then(({ data }) => data);
  }

  exportQuoteItemsMargins(
    quoteId: number,
    revisionId: number
  ): Promise<IQuoteData> {
    return this.axios
      .get<IQuoteData>(
        `${this.endpoint}${quoteId}/revisions/${revisionId}/export-items-margins/`,
        {
          responseType: 'blob',
          timeout: 60 * 1000 * 1 //1 mins
        }
      )
      .then(({ data }) => data);
  }

  fetchQuoteActivities(
    quoteId: number,
    revisionId: number
  ): Promise<Transition[]> {
    return this.axios
      .get<Transition[]>(
        `${this.endpoint}${quoteId}/revisions/${revisionId}/activities/`
      )
      .then(({ data }) => data);
  }

  // Update specific quote parameters
  setQuoteState(id: number, state: number) {
    return this.axios
      .put(`${this.endpoint}${id}/set_state/${state}/`)
      .then(({ data }) => data);
  }

  setQuoteOwner(id: number, user: number) {
    return this.axios
      .put(`${this.endpoint}${id}/set_owner/`, {
        owner: user
      })
      .then(({ data }) => data);
  }

  downloadRfqAttachment(id: number) {
    return this.axios
      .get(`${this.endpoint}${id}/download-rfq-attachment/`, {
        responseType: 'blob'
      })
      .then(({ data }) => data);
  }

  uploadRfqAttachment(id: number, file: File) {
    const formData = objectToFormData({ file_upload: file });
    return this.axios
      .post(`${this.endpoint}${id}/upload-rfq-attachment/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then(({ data }) => data);
  }

  setQuoteInsideSalesOwner(id: number, user: number) {
    return this.axios
      .put(`${this.endpoint}${id}/set_inside_sales_owner/`, {
        inside_sales_owner: user
      })
      .then(({ data }) => data);
  }

  setQuoteCommissionPercent(id: number, commission_percent: number) {
    return this.axios
      .put(`${this.endpoint}${id}/set_commission/`, {
        commission_percent
      })
      .then(({ data }) => data);
  }

  setQuoteTargetDiscountPercent(id: number, percentage: number) {
    return this.axios
      .put(`${this.endpoint}${id}/set_target_discount_percent/`, {
        target_discount_percent: percentage
      })
      .then(({ data }) => data);
  }

  setQuoteRegion(id: number, region: number) {
    return this.axios
      .put(`${this.endpoint}${id}/set_region/`, {
        region
      })
      .then(({ data }) => data);
  }

  setQuoteAgent(id: number, agent: number) {
    return this.axios
      .put(`${this.endpoint}${id}/set_agent/`, {
        agent
      })
      .then(({ data }) => data);
  }

  setQuoteComments(id: number, comments: string) {
    return this.axios
      .put(`${this.endpoint}${id}/add_comments/`, {
        comments
      })
      .then(({ data }) => data);
  }

  setCustomerRelationshipOwner(id: number, owner: string) {
    return this.axios
      .put(`${this.endpoint}${id}/set_customer_relationship_owner/`, {
        customer_relationship_owner: owner
      })
      .then(({ data }) => data);
  }

  setBiddingEnvironment(id: number, environment: string) {
    return this.axios
      .put(`${this.endpoint}${id}/set_bidding_environment/`, {
        bidding_environment: environment
      })
      .then(({ data }) => data);
  }

  setClassification(id: number, classification: number) {
    return this.axios
      .put(`${this.endpoint}${id}/set_classification/`, {
        classification: classification
      })
      .then(({ data }) => data);
  }

  importQuoteVentilationXls(id: number, file: File) {
    const formData = objectToFormData({ file });
    return this.axios
      .post<IQuoteData>(`${this.endpoint}${id}/upload/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 1000 * 60 * 5 //5mins
      })
      .then(({ data }) => data);
  }

  exportQuoteProposal(id: number, language: string) {
    return this.axios
      .get(`${this.endpoint}${id}/export_proposal/`, {
        headers: {
          'Accept-Language': language
        },
        responseType: 'blob',
        timeout: 60 * 1000
      })
      .then(({ data }) => data);
  }

  exportQuoteXML(options?: IQuoteXMLOptions) {
    return this.axios
      .get(`${this.endpoint}export/`, {
        responseType: 'blob',
        timeout: 60 * 1000 * 10, //10 mins
        params: options
      })
      .then(({ data }) => data);
  }

  exportQuoteProformaInvoice(
    id: number,
    orderNumber: string,
    poNumber: string,
    hsCode: string,
    originCountry: string,
    deliveryMode: string,
    exfactoryDate: Date,
    language: string,
    showHsCode: boolean,
    showCoo: boolean
  ) {
    return this.axios
      .get(`v1/proforma-invoices/export/`, {  // TODO RC 2025-09-23: Move proforma invoice logic to a dedicated service
        headers: {
          'Accept-Language': language
        },
        responseType: 'blob',
        timeout: 60 * 1000,
        params: {
          quote: id,
          order_number: orderNumber,
          po_number: poNumber,
          hs_code: hsCode,
          show_hs_code: showHsCode ? 'true' : undefined,
          show_coo: showCoo ? 'true' : undefined,
          origin_country: originCountry,
          delivery_mode: deliveryMode,
          exfactory_date: exfactoryDate
        }
      })
      .then(({ data }) => data);
  }

  exportQuoteOrderReview(id: number, language: string) {
    return this.axios
      .get<Blob>(`${this.endpoint}${id}/export_order_review/`, {
        headers: {
          'Accept-Language': language
        },
        responseType: 'blob',
        timeout: QUOTE_EXPORT_TIMEOUT_IN_MILLISECONDS
      })
      .then(({ data }) => data);
  }

  createOrder(id: number, order: string, customer: string) {
    return this.axios
      .post(`${this.endpoint}${id}/create_order/`, {
        series: order,
        customer: customer
      })
      .then(({ data }) => data);
  }

  // Quote Configuration Item
  updateConfigurationItemDiscount(
    id: number,
    revision: number,
    quoteProductCategory: number,
    item: IConfigurationItemData
  ): Promise<IConfigurationItemData> {
    return this.axios
      .post<IConfigurationItemData>(
        `${this.endpoint}${id}/revisions/${revision}/categories/${quoteProductCategory}/items/${item.id}/apply_discount/`,
        { unit_discount: item.unit_discount }
      )
      .then(({ data }) => data);
  }
  updateConfigurationItemOrder(
    id: number,
    revision: number,
    quoteProductCategory: number,
    item: IConfigurationItemData
  ): Promise<IConfigurationItemData> {
    return this.axios
      .post<IConfigurationItemData>(
        `${this.endpoint}${id}/revisions/${revision}/categories/${quoteProductCategory}/items/${item.id}/apply_order/`,
        { order: item.order }
      )
      .then(({ data }) => data);
  }

  updateConfigurationItemQuantity(
    id: number,
    revision: number,
    quoteProductCategory: number,
    item: IConfigurationItemData
  ): Promise<IConfigurationItemData> {
    return this.axios
      .post<IConfigurationItemData>(
        `${this.endpoint}${id}/revisions/${revision}/categories/${quoteProductCategory}/items/${item.id}/apply_quantity/`,
        { quantity: item.quantity }
      )
      .then(({ data }) => data);
  }

  updateConfigurationItemUnitPrice(
    id: number,
    revision: number,
    quoteProductCategory: number,
    item: IConfigurationItemData
  ): Promise<IConfigurationItemData> {
    return this.axios
      .post<IConfigurationItemData>(
        `${this.endpoint}${id}/revisions/${revision}/categories/${quoteProductCategory}/items/${item.id}/apply_unit_price/`,
        { unit_price: item.unit_price }
      )
      .then(({ data }) => data);
  }

  updateConfigurationItemUnitCost(
    id: number,
    revision: number,
    quoteProductCategory: number,
    item: IConfigurationItemData
  ): Promise<IConfigurationItemData> {
    return this.axios
      .post<IConfigurationItemData>(
        `${this.endpoint}${id}/revisions/${revision}/categories/${quoteProductCategory}/items/${item.id}/apply_unit_cost/`,
        { unit_cost: item.unit_cost }
      )
      .then(({ data }) => data);
  }

  fetchQuoteConfigurationItems(
    id: number,
    revision: number,
    quoteProductCategory: number,
    language?: string
  ): Promise<IConfigurationItemData[]> {
    return this.axios
      .get<IConfigurationItemData[]>(
        `${this.endpoint}${id}/revisions/${revision}/categories/${quoteProductCategory}/items/`,
        {
          headers: {
            'Accept-Language': language
          }
        }
      )
      .then(({ data }) => data);
  }

  applyGlobalDiscountPercentage(
    id: number,
    revision: number,
    category: number,
    global_discount_percentage: number,
    accessory_discount_percentage: number,
    configuration?: number
  ): Promise<any> {
    return this.axios
      .post<any>(
        `${this.endpoint}${id}/revisions/${revision}/categories/${category}/apply_global_discount_percentage/`,
        {
          // Need to cast to Number for type to be
          // serialzied properly at the server side.
          global_discount_percentage: Number(global_discount_percentage),
          accessory_discount_percentage: Number(accessory_discount_percentage)
        },
        {
          params: { configuration }
        }
      )
      .then(({ data }) => data);
  }

  applyFirstYearSubscriptionDiscountPercentage(
    id: number,
    revision: number,
    category: number,
    first_year_subscription_discount_percentage: number
  ): Promise<void> {
    return this.axios
      .post<void>(
        `${this.endpoint}${id}/revisions/${revision}/categories/${category}/apply_first_year_subscription_discount_percentage/`,
        {
          first_year_subscription_discount_percentage
        }
      )
      .then(({ data }) => data);
  }

  // Evaluate Price Quote Service
  evaluateComputationPrice(id: number, revision: number, computation: number) {
    return this.axios
      .get(
        `${this.endpoint}${id}/revisions/${revision}/evaluate-service-price/`,
        {
          params: {
            computation
          }
        }
      )
      .then(({ data }) => data);
  }

  evaluateComputationDays(id: number, revision: number, computation: number) {
    return this.axios
      .get(
        `${this.endpoint}${id}/revisions/${revision}/evaluate-service-days/`,
        {
          params: {
            computation
          }
        }
      )
      .then(({ data }) => data);
  }

  calculateCoreDiscountPercent(
    id: number,
    data: IQuoteCalculateCoreDiscountPercentParams
  ): Promise<{ core_discount_percent: number }> {
    return this.axios
      .post(`${this.endpoint}${id}/calculate_core_discount_percent/`, data)
      .then(({ data }) => data);
  }

  applyDiscountPercentages(
    id: number,
    data: IQuoteApplyDiscountPercentagesParams
  ): Promise<IQuoteData> {
    return this.axios
      .post(`${this.endpoint}${id}/apply_discounts/`, data)
      .then(({ data }) => data);
  }

  applySurcharges(
    id: number,
    applicationType: QuoteSurchargeApplicationTypeEnum
  ): Promise<IQuoteData> {
    return this.axios
      .post(`${this.endpoint}${id}/apply-surcharges/`, {
        surcharge_application_type: applicationType
      })
      .then(({ data }) => data);
  }

  applySurchargeDiscountPercentage(
    id: number,
    discountPercent: number
  ): Promise<IQuoteData> {
    return this.axios
      .post(`${this.endpoint}${id}/apply-surcharge-discount-percent/`, {
        surcharge_discount_percent: discountPercent
      })
      .then(({ data }) => data);
  }

  evaluateSurcharges(
    id: number
  ): Promise<{
    total_surcharge_all_items: number;
    total_surcharge_mandatory_items: number;
  }> {
    return this.axios
      .get(`${this.endpoint}${id}/evaluate-surcharges/`)
      .then(({ data }) => data);
  }

  /**
   * Create a Salesforce opportunity for the quote.
   * @param id
   */
  createSalesforceOpportunity(
    id: number
  ): Promise<{ salesforce_opportunity_id: number }> {
    return this.axios
      .post(`${this.endpoint}${id}/create-salesforce-opportunity/`)
      .then(({ data }) => data);
  }

  //Packages
  fetchQuotePackages(
    filter?: IQuotePackageParams
  ): Promise<IQuotePackageData[]> {
    return this.axios
      .get(`v1/quotes/packages/`, { params: filter })
      .then(({ data }) => data);
  }

  fetchQuotePackage(id: number): Promise<IQuotePackageData> {
    return this.axios.get(`v1/quotes/packages/${id}/`).then(({ data }) => data);
  }

  createQuotePackage(data: IQuotePackageData): Promise<IQuotePackageData> {
    return this.axios
      .post(`v1/quotes/packages/`, data)
      .then(({ data }) => data);
  }

  updateQuotePackage(data: IQuotePackageData): Promise<IQuotePackageData> {
    return this.axios
      .patch(`v1/quotes/packages/${data.id}/`, data)
      .then(({ data }) => data);
  }

  setQuotePackageQuotes(
    packageId: number,
    quotes: number[]
  ): Promise<IQuotePackageData> {
    return this.axios
      .put(`v1/quotes/packages/${packageId}/set-quotes/`, {
        quotes: quotes
      })
      .then(({ data }) => data);
  }

  createQuoteProjectDocument(
    data: IQuotePackageDocument
  ): Promise<IQuotePackageDocumentData> {
    const formData = objectToFormData(data);
    return this.axios
      .post(`v1/quotes/packages/${data.quote_package}/documents/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then(({ data }) => data);
  }

  exportQuotePackage(id: number, language: string): Promise<Blob> {
    return this.axios
      .get(`v1/quotes/packages/${id}/export_package/`, {
        headers: {
          'Accept-Language': language
        },
        responseType: 'blob',
        timeout: 60 * 1000
      })
      .then(({ data }) => data);
  }
}
